import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Link2, CalendarDays } from "lucide-react";
import ArticleBody from "@/components/ArticleBody";
import { usePageMeta } from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const prettyDate = (stamp) => {
  try {
    return new Date(stamp).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

const applyPageMeta = (data) => {
  document.title = `${data.title} | Vagus Digital Insights`;
  const meta = document.querySelector('meta[name="description"]');
  if (meta) meta.setAttribute("content", data.summary);
  const existing = document.getElementById("article-jsonld");
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = "article-jsonld";
  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: data.title,
    description: data.summary,
    image: data.cover_image,
    datePublished: data.published_at,
    author: { "@type": "Organization", name: "Vagus Digital" },
    publisher: { "@type": "Organization", name: "Vagus Digital" },
  });
  document.head.appendChild(script);
};

const ArticlePage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);
  const [related, setRelated] = useState([]);
  const [copied, setCopied] = useState(false);

  usePageMeta(post ? `${post.title} | Vagus Digital Insights` : "Vagus Digital | Insights");

  useEffect(() => {
    let active = true;
    setPost(null);
    setMissing(false);
    fetch(`${API}/posts/${slug}`)
      .then(async (res) => {
        if (res.status === 404) throw new Error("missing");
        return res.json();
      })
      .then((data) => {
        if (!active) return;
        setPost(data);
        applyPageMeta(data);
        fetch(`${API}/posts`)
          .then((r) => r.json())
          .then((d) => {
            if (!active) return;
            const others = (d.posts || []).filter((p) => p.slug !== slug);
            const sameTag = others.filter((p) => p.tag === data.tag);
            const rest = others.filter((p) => p.tag !== data.tag);
            setRelated([...sameTag, ...rest].slice(0, 3));
          })
          .catch(() => {});
      })
      .catch(() => {
        if (active) setMissing(true);
      });
    return () => {
      active = false;
    };
  }, [slug]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareLinks = post
    ? [
        {
          label: "LinkedIn",
          href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
          testid: "share-linkedin",
        },
        {
          label: "X",
          href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`,
          testid: "share-x",
        },
        {
          label: "WhatsApp",
          href: `https://wa.me/?text=${encodeURIComponent(`${post.title}: ${shareUrl}`)}`,
          testid: "share-whatsapp",
        },
      ]
    : [];

  if (missing) {
    return (
      <section className="flex min-h-screen items-center justify-center bg-paper px-6 pt-24 text-center">
        <div data-testid="article-missing">
          <h1 className="font-display text-3xl font-light text-ink">This piece isn't published.</h1>
          <Link to="/insights" data-testid="missing-insights-link" className="mt-6 inline-block text-sm font-medium text-jade underline-offset-4 hover:underline">
            Back to Insights
          </Link>
        </div>
      </section>
    );
  }

  if (!post) {
    return (
      <section className="flex min-h-screen items-center justify-center bg-paper pt-24">
        <p className="text-ink2" data-testid="article-loading">Loading…</p>
      </section>
    );
  }

  return (
    <article className="min-h-screen bg-paper pb-24 pt-32 lg:pt-40">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="mx-auto max-w-3xl px-6 lg:px-10"
        data-testid="article-page"
      >
        <Link
          to="/insights"
          data-testid="article-back-link"
          className="inline-flex items-center gap-2 text-sm font-medium text-jade transition-opacity duration-300 hover:opacity-70"
        >
          <ArrowLeft className="h-4 w-4" /> All insights
        </Link>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <span className="rounded-full border border-jade/20 px-3 py-1 text-xs text-jade">
            {post.tag}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-ink2/60">
            <CalendarDays className="h-3.5 w-3.5" />
            {prettyDate(post.published_at)} · {post.reading_time} min read · Vagus Digital
          </span>
        </div>

        <h1 className="mt-5 font-display text-3xl font-light leading-tight tracking-tight text-ink sm:text-4xl lg:text-5xl" data-testid="article-title">
          {post.title}
        </h1>
        <p className="mt-4 text-lg leading-relaxed text-ink2" data-testid="article-summary">
          {post.summary}
        </p>

        <div className="mt-10 overflow-hidden rounded-2xl shadow-ambient">
          <img src={post.cover_image} alt={post.title} className="aspect-[3/2] w-full object-cover" />
        </div>

        <div className="mt-6">
          <ArticleBody body={post.body} />
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-jade/10 pt-8" data-testid="share-row">
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-ink2/60">
            Share
          </span>
          {shareLinks.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={s.testid}
              className="rounded-full border border-jade/25 px-5 py-2 text-sm font-medium text-jade transition-colors duration-300 hover:bg-jade/5"
            >
              {s.label}
            </a>
          ))}
          <button
            onClick={() => {
              navigator.clipboard?.writeText(shareUrl);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            data-testid="share-copy-link"
            className="rounded-full border border-jade/25 px-5 py-2 text-sm font-medium text-jade transition-colors duration-300 hover:bg-jade/5"
          >
            {copied ? "Copied" : "Copy link"}
          </button>
        </div>

        {related.length > 0 && (
          <div className="mt-16 border-t border-jade/10 pt-10" data-testid="related-row">
            <h2 className="font-display text-2xl font-medium text-ink">Related reading</h2>
            <ul className="mt-6 space-y-4">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`/insights/${r.slug}`}
                    data-testid={`related-${r.slug}`}
                    className="group flex items-center justify-between gap-4 rounded-2xl bg-white p-6 shadow-ambient transition-colors duration-300 hover:bg-paper2/60"
                  >
                    <div>
                      <p className="font-display text-xl font-medium text-ink">{r.title}</p>
                      <p className="mt-1 text-sm text-ink2/70">{r.summary}</p>
                    </div>
                    <ArrowUpRight className="h-5 w-5 shrink-0 text-jade/60 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </motion.div>
    </article>
  );
};

export default ArticlePage;